import React, { useState, useEffect, useMemo } from 'react';
import { StateSelect, ZipCodeInput, CountySelect } from '../../../common/SharedComponents';
import { NumericFormat } from 'react-number-format';
import { FloodReport } from '../LoanComponents';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RentalHoldbackReleasesGrid, RepaymentsGrids } from '../LoanComponents';
import { formatDateTime, buildTypes, buildTypeString } from '@apply/ui';
import { LoanFilesGrid } from '../LoanComponents';
import {
    LoanPropertyFileTypeOptions,
    LoanType,
    FloodAreaOptions,
    StructureType,
    StructureTypeOptions,
    RepaymentLevelType,
    LoanPropertyPurposeTypeOptions,
    NoticeStatusType,
    LoanStatusType,
    FeatureType,
    PropertyDevelopmentType,
    PropertyDevelopmentTypeOptions,
    CertificateOfOccupancyTypeOptions,
    ExitType,
    ExitTypeOptions,
    LoanPropertyHadPermitAtClosingTypeOptions,
    PreferredInterestPaymentTypeOptions,
} from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faPencil, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { AppraisalsGrid } from './AppraisalsGrid';
import { DrawPanel } from '../../modals/EditStdLoanModal/Draws/DrawPanel';
import { useSelector } from 'react-redux';

export const LoanProperty = ({
    bfiNumber,
    property,
    onChange,
    loanTypeId,
    statusId,
    commitment,
    validateSubmission,
    isPortfolio = false,
    required = true,
    visible = true,
}) => {
    const styles = {
        formRow: { marginBottom: '0' },
        formColumn: { paddingBottom: '3px' },
        propertyTitle: {
            backgroundColor: 'aliceblue',
            margin: '0',
            padding: '5px',
            marginBottom: '10px',
            position: 'relative',
        },
        displayFlex: { display: 'flex' },
        iconButton: { marginLeft: '5px', marginRight: '45px' },
    };

    const [canOrderFloodReport, set_canOrderFloodReport] = useState(false);
    const [showLegalDescriptionAlert, set_showLegalDescriptionAlert] = useState(false);
    const [legalDescriptionAlertText, set_legalDescriptionAlertText] = useState('');

    const userState = useSelector((state) => state.user);

    const loanApi = new LoanApi.LoanApi(applicationInsights);

    const isFeatureEnabled = (featureId) => {
        if (!userState || !userState.featureList) {
            return false;
        }
        const feature = userState.featureList.find((f) => f.featureId === featureId);
        return feature?.isEnabled ?? false;
    };

    const areDrawsEnabled = useMemo(() => isFeatureEnabled(FeatureType.Draws), [isFeatureEnabled]);

    useEffect(() => {
        if (!property) {
            return;
        }

        if (!property.floodReport) {
            property.floodReport = {};
        }

        //Enable/Disable Flood Report Ordering
        const canOrder =
            !!bfiNumber && !!property && !!property.street1 && !!property.city && !!property.state && !!property.zipCode;
        if (!canOrder) {
            property.floodReport.orderReport = false;
        }
        set_canOrderFloodReport(canOrder);
    }, [bfiNumber, property]);

    const statusOptions = () => {
        if (loanTypeId === LoanType.BuildRent) {
            return [{ name: 'Select...' }, { name: 'Build-Rent' }, { name: 'Fix-Rent' }];
        } else {
            return [{ name: 'Select...' }, { name: 'Spec.' }, { name: 'Pre-Sold' }];
        }
    };

    const getUnitCountOptionsByStructureType = (structureTypeId) => {
        switch (structureTypeId) {
            case StructureType.DetachedHouse:
            case StructureType.Townhouse:
            case StructureType.CondoInStructure:
                return [1];
            case StructureType.Duplex:
                return [1, 2];
            case StructureType.Triplex:
                return [1, 2, 3];
            case StructureType.Quadplex:
                return [1, 2, 3, 4];
            default:
                return []; //Indicates 5 + Unit (free number entry)
        }
    };

    const _updateStructureTypeId = (input) => {
        if (!property) {
            return;
        }

        var newId = parseInt(input);
        if (property.structureTypeId !== newId) {
            property.structureTypeId = newId;
            var unitCounts = getUnitCountOptionsByStructureType(newId);
            if (unitCounts.length > 0) {
                property.unitCount = unitCounts[unitCounts.length - 1];
            } else {
                property.unitCount = null;
            }
            onChange && onChange(property);
        }
    };

    const _updateLegalDescription = (input) => {
        if (input.length > 500) {
            set_showLegalDescriptionAlert(true);
            set_legalDescriptionAlertText(`Shorten the field by ${input.length - 500} characters.`);
        } else {
            set_showLegalDescriptionAlert(false);
            set_legalDescriptionAlertText('');
        }
        property.legalDescription = input;
        onChange && onChange({ ...property });
    };

    const _updateStreet1 = (input) => {
        property.street1 = input;
        onChange && onChange({ ...property });
    };

    const _updateStreet2 = (input) => {
        property.street2 = input;
        onChange && onChange({ ...property });
    };

    const _updateCity = (input) => {
        property.city = input;
        onChange && onChange({ ...property });
    };

    const _updateState = (input) => {
        property.state = input;
        onChange && onChange({ ...property });
    };

    const _updateZipcode = (input) => {
        property.zipCode = input;
        onChange && onChange({ ...property });
    };

    const _updateCounty = (input) => {
        property.county = input;
        onChange && onChange({ ...property });
    };

    const _updateUnitCount = (input) => {
        let cleanedInput = input
            .toString()
            .replace(/[^0-9.]/g, '')
            .replace(/(\..*)\./g, '$1');
        if (cleanedInput) {
            cleanedInput = parseInt(cleanedInput);
        }

        property.unitCount = cleanedInput;
        onChange && onChange({ ...property });
    };

    const _updateIncomeRestrictedUnits = (input) => {
        let cleanedInput = input
            .toString()
            .replace(/[^0-9.]/g, '')
            .replace(/(\..*)\./g, '$1');
        if (cleanedInput) {
            cleanedInput = parseInt(cleanedInput);
        }

        property.unitsIncomeRestricted = cleanedInput;
        onChange && onChange({ ...property });
    };

    const updateStatus = (value) => {
        property.status = value;
        onChange && onChange({ ...property });
    };

    const updateHadPermitAtClosing = (input) => {
        property.hadPermitAtClosingTypeId = input === '' ? null : parseInt(input);
        onChange && onChange({ ...property });
    };

    const _updateLandCost = (input) => {
        property.landCost = input;
        onChange && onChange({ ...property });
    };

    const _updateVerticalCost = (input) => {
        property.verticalCost = input;
        onChange && onChange({ ...property });
    };

    const _updateCommitment = (input) => {
        property.commitment = input;
        onChange && onChange({ ...property });
    };

    const _updateOriginationFee = (input) => {
        property.originationFee = input;
        onChange && onChange({ ...property });
    };

    const _updateThreeMilePopulation = (input) => {
        property.threeMilePopulation = input;
        onChange && onChange({ ...property });
    };

    const setInSpecialFloodHazardArea = (input) => {
        property.inSpecialFloodHazardArea = input === 1;
        onChange && onChange({ ...property });
    };

    const setDoNotSendFloodNotice = (input) => {
        property.doNotSendFloodNotice = input === 1;
        onChange && onChange({ ...property });
    };

    const _updateThreeMileFiveYearGrowth = (input) => {
        property.threeMileFiveYearGrowth = input;
        onChange && onChange({ ...property });
    };

    const _updateRentalHoldback = (input) => {
        property.rentalHoldback = input;
        onChange && onChange({ ...property });
    };

    const _updatePurposeTypeId = (input) => {
        property.loanPropertyPurposeTypeId = input;
        onChange && onChange({ ...property });
    };

    const _updateCurrentDebt = (input) => {
        property.currentDebt = input;
        onChange && onChange({ ...property });
    };

    const _updateIsLoanProceedsUsedPropertyImprovements = (input) => {
        property.isLoanProceedsUsedPropertyImprovements = input;
        onChange && onChange({ ...property });
    };

    const _updateMoreThan10kCapExpenditures = (input) => {
        property.moreThan10kExpenditures = input;
        onChange && onChange({ ...property });
    };

    const _updateIsMajorRehab = (input) => {
        property.isMajorRehab = input;
        onChange && onChange({ ...property });
    };

    const _updatePropertyDevelopmentTypeId = (input) => {
        property.propertyDevelopmentTypeId = input === '' ? null : parseInt(input);
        onChange && onChange({ ...property });
    };

    const _updatePropertyDevelopmentTypeOther = (input) => {
        property.propertyDevelopmentTypeOther = input;
        onChange && onChange({ ...property });
    };

    const _updateYearBuilt = (input) => {
        let cleanedInput = input.toString().replace(/[^0-9]/g, '');
        property.yearBuilt = cleanedInput ? parseInt(cleanedInput) : null;
        onChange && onChange({ ...property });
    };

    const _updateOwnedThroughDevelopment = (input) => {
        property.ownedThroughDevelopment = input;
        onChange && onChange({ ...property });
    };

    const _updateBuildTypeId = (input) => {
        property.buildTypeId = input === '' ? null : parseInt(input);
        onChange && onChange({ ...property });
    };

    const _updateCertificateOfOccupancyTypeId = (input) => {
        property.certificateOfOccupancyTypeId = input === '' ? null : parseInt(input);
        onChange && onChange({ ...property });
    };

    const _updateWorkNeeded = (input) => {
        property.workNeeded = input;
        onChange && onChange({ ...property });
    };

    const _updateWorkNeededDescription = (input) => {
        property.workNeededDescription = input;
        onChange && onChange({ ...property });
    };

    const _updateExpectedListingPrice = (input) => {
        property.expectedListingPrice = input;
        onChange && onChange({ ...property });
    };

    const _updateExitType = (input) => {
        property.exitType = input === '' ? null : parseInt(input);
        onChange && onChange({ ...property });
    };

    const _updateExpectedTakeOutLoanAmount = (input) => {
        property.expectedTakeOutLoanAmount = input;
        onChange && onChange({ ...property });
    };

    const _updateHoaDues = (input) => {
        property.hoaDues = input;
        onChange && onChange({ ...property });
    };

    const _updatePreferredInterestPaymentType = (input) => {
        property.preferredInterestPaymentTypeId = input === '' ? null : parseInt(input);
        onChange && onChange({ ...property });
    };

    const onUpdateIsProblemProperty = (input) => {
        property.isProblemProperty = input;
        onChange && onChange({ ...property });
    };

    const sortAppraisals = (apprs) => {
        if (!apprs) {
            return [];
        }
        return apprs.sort(function (a, b) {
            // Turn your strings into dates, and then subtract them
            // to get a value that is either negative, positive, or zero.
            return new Date(b.appraisedDate) - new Date(a.appraisedDate);
        });
    };

    const getLastAppraisalValue = (property) => {
        if (!property || !property.appraisals) {
            return null;
        }
        const sorted = sortAppraisals(property.appraisals);
        if (sorted.length === 0) {
            return null;
        }
        return sorted[0].appraisedValue;
    };

    const getLtv = () => {
        const value = getLastAppraisalValue(property);
        if (isPortfolio) {
            return value && property?.commitment ? ((property.commitment / value) * 100).toFixed(1) + '%' : '';
        } else {
            return value && commitment ? ((commitment / value) * 100).toFixed(1) + '%' : '';
        }
    };

    const onFloodReportCancel = async () => {
        var response = await loanApi.cancelFloodReport(property.id);
        if (response.statusCode !== 200) {
            return;
        }
        property.floodReport = response.result;
        onChange && onChange({ ...property });
    };

    const onFloodReportChange = (fr) => {
        property.floodReport = fr;
        onChange && onChange({ ...property });
    };

    const onAppraisalsChange = (apprs) => {
        property.appraisals = apprs;
        onChange && onChange({ ...property });
    };

    const onPropertyFilesChanges = (files) => {
        property.files = files;
        onChange && onChange({ ...property });
    };

    const getFloodNoticeStyle = () => {
        if (property.doNotSendFloodNotice) {
            return { paddingTop: '8px', color: 'gray' };
        }
        switch (property.floodNoticeStatusId) {
            case NoticeStatusType.Emailed:
            case NoticeStatusType.Generated:
                return { paddingTop: '8px', color: 'green' };
            case NoticeStatusType.Pending:
                if (!property.inSpecialFloodHazardArea) {
                    return { paddingTop: '8px', color: 'gray' };
                } else {
                    return { paddingTop: '8px', color: 'darkred' };
                }
            case NoticeStatusType.Error:
                return { paddingTop: '8px', color: 'darkred' };
            default:
                if (!property.oldInSpecialFloodHazardArea && property.inSpecialFloodHazardArea) {
                    return { paddingTop: '8px', color: 'darkred' };
                } else {
                    return { paddingTop: '8px', color: 'gray' };
                }
        }
    };

    const willTriggerFloodNotice = () => {
        switch (property.floodNoticeStatusId) {
            case NoticeStatusType.Pending:
                if (property.inSpecialFloodHazardArea) {
                    return true;
                }
            default:
                if (!property.oldInSpecialFloodHazardArea && property.inSpecialFloodHazardArea) {
                    return true;
                }
        }
        return false;
    };

    const getFloodNoticeTitle = () => {
        switch (property.floodNoticeStatusId) {
            case NoticeStatusType.Emailed:
                return `The notice 'Flood Report' was sent at ${formatDateTime(property.floodNoticeDate)}`;
            case NoticeStatusType.Generated:
                return `The notice 'Flood Report' has been generated but not yet emailed`;
            case NoticeStatusType.Pending:
                if (!property.inSpecialFloodHazardArea || property.doNotSendFloodNotice) {
                    return "The notice 'Flood Report' will be unscheduled";
                } else {
                    return `The notice 'Flood Report' will be sent at ${formatDateTime(property.floodNoticeDate)}`;
                }
            case NoticeStatusType.Error:
                return "The notice 'Flood Report' experiences an Error";
            default:
                if (
                    !property.oldInSpecialFloodHazardArea &&
                    property.inSpecialFloodHazardArea &&
                    !property.doNotSendFloodNotice
                ) {
                    return "The notice 'Flood Report' will be scheduled to be sent after Save";
                }
                return 'Current changes will not trigger a notice.';
        }
    };

    if (!property || !visible) {
        return '';
    }

    return (
        <div>
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-12'
                    style={styles.formColumn}
                >
                    <label htmlFor={'legalDescription'}>Legal Description {required && <span className='req'>*</span>}</label>
                    <input
                        type='text'
                        id={'legalDescription'}
                        value={property?.legalDescription ?? ''}
                        onChange={(event) => _updateLegalDescription(event.target.value)}
                        required={required}
                    />
                    {showLegalDescriptionAlert && (
                        <>
                            <FontAwesomeIcon
                                id='legalDescriptionAlert'
                                icon={faExclamationTriangle}
                                color='red'
                                style={{ float: 'left', paddingRight: '5px' }}
                            />
                            <label
                                style={{ color: 'red' }}
                                htmlFor={'legalDescription'}
                            >
                                {legalDescriptionAlertText}
                            </label>
                        </>
                    )}
                </div>
            </div>
            <div
                className='row'
                style={styles.formRow}
            >
                {loanTypeId !== LoanType.Land && (
                    <div
                        className='columns small-12 large-4'
                        style={styles.formColumn}
                    >
                        <label htmlFor={'structureTypeId'}>Structure Type {required && <span className='req'>*</span>}</label>
                        <select
                            id={'structureTypeId'}
                            value={property?.structureTypeId ?? ''}
                            onChange={(event) => _updateStructureTypeId(parseInt(event.target.value))}
                            required={required}
                        >
                            <option value=''></option>
                            {StructureTypeOptions.map((o) => {
                                return (
                                    <option
                                        key={o.id}
                                        value={o.id}
                                    >
                                        {o.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}
                <div
                    className='columns small-12 large-2'
                    style={styles.formColumn}
                >
                    <label
                        htmlFor={'unitCount'}
                        style={{ paddingRight: '0' }}
                    >
                        {loanTypeId === LoanType.Land ? 'Eventual Parcels' : 'Collateral Units'}
                        &nbsp;{required && <span className='req'>*</span>}
                    </label>
                    {loanTypeId !== LoanType.Land && property?.structureTypeId !== 6 && (
                        <select
                            id={'unitCount'}
                            value={property?.unitCount ?? 1}
                            onChange={(event) => _updateUnitCount(parseInt(event.target.value))}
                            required={required}
                        >
                            {getUnitCountOptionsByStructureType(property?.structureTypeId).map((i) => {
                                return (
                                    <option
                                        key={i}
                                        value={i}
                                    >
                                        {i}
                                    </option>
                                );
                            })}
                        </select>
                    )}
                    {(loanTypeId === LoanType.Land || property?.structureTypeId === 6) && (
                        <input
                            type='text'
                            id={'unitCount'}
                            value={property?.unitCount ?? ''}
                            onChange={(event) => _updateUnitCount(event.target.value)}
                            required={required}
                            maxLength={3}
                        />
                    )}
                </div>
                {(loanTypeId === LoanType.BuildRent ||
                    loanTypeId === LoanType.Rental ||
                    loanTypeId === LoanType.FixFlip ||
                    loanTypeId === LoanType.SubNoteFixFlip ||
                    loanTypeId === LoanType.Bridge) && (
                    <div
                        className='columns small-12 large-2'
                        style={styles.formColumn}
                    >
                        <label
                            htmlFor={'unitsIncomeRestricted'}
                            style={{ paddingRight: '0' }}
                        >
                            Income Restricted Units
                        </label>
                        <input
                            type='text'
                            id={'unitsIncomeRestricted'}
                            value={property?.unitsIncomeRestricted ?? ''}
                            onChange={(event) => _updateIncomeRestrictedUnits(event.target.value)}
                            required={false}
                            maxLength={3}
                        />
                    </div>
                )}
            </div>
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-7'
                    style={styles.formColumn}
                >
                    <label htmlFor={'street1'}>Street Address {required && <span className='req'>*</span>}</label>
                    <input
                        id={'street1'}
                        type='text'
                        value={property?.street1 ?? ''}
                        onChange={(event) => _updateStreet1(event.target.value)}
                        required={required}
                        maxLength={50}
                    />
                </div>
                <div
                    className='columns small-12 large-5'
                    style={styles.formColumn}
                >
                    <label htmlFor={'street2'}>Street Address 2</label>
                    <input
                        id={'street2'}
                        type='text'
                        value={property?.street2 ?? ''}
                        onChange={(event) => _updateStreet2(event.target.value)}
                        maxLength={50}
                    />
                </div>
                <div
                    className='columns small-12 large-4'
                    style={styles.formColumn}
                >
                    <label htmlFor={'city'}>City {required && <span className='req'>*</span>}</label>
                    <input
                        id={'city'}
                        type='text'
                        value={property?.city ?? ''}
                        onChange={(event) => _updateCity(event.target.value)}
                        required={required}
                        maxLength={50}
                    />
                </div>
                <div
                    className='columns small-12 large-2'
                    style={styles.formColumn}
                >
                    <label htmlFor={'state'}>State {required && <span className='req'>*</span>}</label>
                    <StateSelect
                        id={'state'}
                        value={property?.state ?? ''}
                        onChange={(event) => _updateState(event.target.value)}
                        required={required}
                    />
                </div>
                <div
                    className='columns small-12 medium-6 large-2'
                    style={styles.formColumn}
                >
                    <label htmlFor={'zipCode'}>Zip {required && <span className='req'>*</span>}</label>
                    <ZipCodeInput
                        id={'zipCode'}
                        value={property?.zipCode ?? ''}
                        onChange={(event) => _updateZipcode(event.target.value)}
                        required={required}
                    />
                </div>
                <div
                    className='columns small-12 medium-6 large-4'
                    style={styles.formColumn}
                >
                    <label htmlFor={'county'}>County</label>
                    <CountySelect
                        id={'county'}
                        value={property?.county ?? ''}
                        selectedState={property?.state ?? ''}
                        onChange={(event) => _updateCounty(event.target.value)}
                    />
                </div>
            </div>
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-2'
                    style={styles.formColumn}
                >
                    <label htmlFor={'ltv'}>Loan To Value</label>
                    <input
                        type='text'
                        id='ltv'
                        value={getLtv()}
                        disabled={true}
                    />
                </div>
                {loanTypeId !== LoanType.Land && loanTypeId !== LoanType.Rental && (
                    <div
                        className='columns small-12 large-2'
                        style={styles.formColumn}
                    >
                        <label htmlFor={'propStatus'}>Status</label>
                        <select
                            id={'propStatus'}
                            value={property?.status ?? ''}
                            onChange={(e) => updateStatus(e.target.value)}
                        >
                            {statusOptions().map((fs) => {
                                return (
                                    <option
                                        key={fs.name}
                                        value={fs.name}
                                    >
                                        {fs.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}
                {(loanTypeId == LoanType.Construction || loanTypeId == LoanType.FixFlip) && (
                    <div
                        className='columns small-12 large-3'
                        style={styles.formColumn}
                    >
                        <label htmlFor={'permitAtClosing'}>Had Permit at Time of Closing?</label>
                        <select
                            id={'permitAtClosing'}
                            value={property?.hadPermitAtClosingTypeId ?? ''}
                            onChange={(e) => updateHadPermitAtClosing(e.target.value)}
                        >
                            <option value=''></option>
                            {LoanPropertyHadPermitAtClosingTypeOptions.map((o) => {
                                return (
                                    <option
                                        key={o.id}
                                        value={o.id}
                                    >
                                        {o.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}
            </div>
            <div
                className='row'
                style={styles.formRow}
            >
                {loanTypeId !== LoanType.Rental && (
                    <div className='columns small-12 large-2'>
                        <label htmlFor={'landCost'}>Land Cost</label>
                        <NumericFormat
                            thousandSeparator=','
                            prefix='$'
                            id='landCost'
                            name='landCost'
                            placeholder='$'
                            value={property?.landCost ?? ''}
                            decimalScale={0}
                            allowNegative={false}
                            onValueChange={(values) => {
                                _updateLandCost(values.floatValue);
                            }}
                            required={false}
                            isAllowed={(values, sourceInfo) => {
                                const value = values?.floatValue;
                                return value === undefined || value === null || value > 0;
                            }}
                        />
                    </div>
                )}
                {loanTypeId !== LoanType.Rental && (
                    <div className='columns small-12 large-2'>
                        <label htmlFor={'verticalCost'}>
                            {loanTypeId === LoanType.Land ? 'Improvement Cost' : 'Vertical Cost'}
                        </label>
                        <NumericFormat
                            thousandSeparator=','
                            prefix='$'
                            id='verticalCost'
                            name='verticalCost'
                            placeholder='$'
                            value={property.verticalCost ?? ''}
                            decimalScale={0}
                            allowNegative={false}
                            onValueChange={(values) => {
                                _updateVerticalCost(values.floatValue);
                            }}
                            required={false}
                            isAllowed={(values, sourceInfo) => {
                                const value = values?.floatValue;
                                return value === undefined || value === null || value > 0;
                            }}
                        />
                    </div>
                )}
                <div className='columns small-12 large-2'>
                    <label htmlFor={'threeMilePopulation'}>3 Mile Population</label>
                    <NumericFormat
                        thousandSeparator=','
                        id='threeMilePopulation'
                        onValueChange={(values) => {
                            _updateThreeMilePopulation(values.floatValue);
                        }}
                        decimalScale={0}
                        allowNegative={false}
                        value={property.threeMilePopulation ?? ''}
                        displayType='input'
                        isAllowed={(values, sourceInfo) => {
                            const value = values?.floatValue;
                            return value === undefined || value === null || value > 0;
                        }}
                    />
                </div>
                <div className='columns small-12 large-2'>
                    <label htmlFor={'threeMileFiveYearGrowth'}>3 Mile/5 Yr Growth</label>
                    <NumericFormat
                        id='threeMileFiveYearGrowth'
                        onValueChange={(values) => {
                            _updateThreeMileFiveYearGrowth(values.floatValue);
                        }}
                        decimalScale={2}
                        suffix={'%'}
                        placeholder='%'
                        allowNegative={true}
                        value={property.threeMileFiveYearGrowth ?? ''}
                        displayType='input'
                    />
                </div>
                <div
                    className='columns small-12 large-3'
                    style={styles.formColumn}
                >
                    <label htmlFor='loanPropertyPurposeTypeId'>Purpose</label>
                    <select
                        id={'loanPropertyPurposeTypeId'}
                        value={property.loanPropertyPurposeTypeId ?? ''}
                        onChange={(event) => _updatePurposeTypeId(parseInt(event.target.value))}
                        required={false}
                        disabled={loanTypeId === LoanType.BuildRent}
                    >
                        <option value=''></option>
                        {LoanPropertyPurposeTypeOptions.map((o) => {
                            return (
                                <option
                                    key={o.id}
                                    value={o.id}
                                >
                                    {o.name}
                                </option>
                            );
                        })}
                    </select>
                </div>
                {(loanTypeId === LoanType.Rental ||
                    loanTypeId === LoanType.BuildRent ||
                    loanTypeId === LoanType.FixFlip ||
                    loanTypeId === LoanType.SubNoteFixFlip ||
                    loanTypeId === LoanType.Bridge) && (
                    <div className='columns small-12 large-2'>
                        <label htmlFor={'currentDebt'}>Current Debt</label>
                        <NumericFormat
                            thousandSeparator=','
                            prefix='$'
                            id='currentDebt'
                            name='currentDebt'
                            placeholder='$'
                            value={property?.currentDebt ?? ''}
                            decimalScale={0}
                            allowNegative={false}
                            onValueChange={(values) => {
                                _updateCurrentDebt(values.floatValue);
                            }}
                            isAllowed={(values, sourceInfo) => {
                                const value = values?.floatValue;
                                return value === undefined || value === null || value >= 0;
                            }}
                        />
                    </div>
                )}
                {(loanTypeId === LoanType.FixFlip || loanTypeId === LoanType.SubNoteFixFlip) && (
                    <div className='columns small-12 large-3'>
                        <label htmlFor={'isMajorRehab'}>Does Rehab exceed $100,000?</label>
                        <select
                            id={'isMajorRehab'}
                            value={property?.isMajorRehab ? '1' : '0'}
                            onChange={(event) => _updateIsMajorRehab(parseInt(event.target.value) === 1)}
                            style={{ width: '170px' }}
                        >
                            <option value='0'>No</option>
                            <option value='1'>Yes</option>
                        </select>
                    </div>
                )}
                {loanTypeId === LoanType.Rental && (
                    <>
                        <div className='columns small-12 large-3'>
                            <label htmlFor={'isLoanProceedsUsedPropertyImprovements'}>Proceeds to Improve? (HMDA)</label>
                            <select
                                id={'isLoanProceedsUsedPropertyImprovements'}
                                value={property?.isLoanProceedsUsedPropertyImprovements ? '1' : '0'}
                                onChange={(event) =>
                                    _updateIsLoanProceedsUsedPropertyImprovements(parseInt(event.target.value) === 1)
                                }
                                disabled={true}
                                required={false}
                            >
                                <option value='0'>No</option>
                                <option value='1'>Yes</option>
                            </select>
                        </div>
                        <div className='columns small-12 large-3'>
                            <label htmlFor={'moreThan10kExpenditures'}>Over $20,000 of Cap. Exp. (HMDA)</label>
                            <select
                                id={'moreThan10kExpenditures'}
                                value={property?.moreThan10kExpenditures ? '1' : '0'}
                                onChange={(event) => _updateMoreThan10kCapExpenditures(parseInt(event.target.value) === 1)}
                                disabled={true}
                                required={false}
                            >
                                <option value='0'>No</option>
                                <option value='1'>Yes</option>
                            </select>
                        </div>
                    </>
                )}
            </div>
            {loanTypeId === LoanType.Bridge && (
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div className='columns small-12 large-3'>
                        <label htmlFor='propertyDevelopmentTypeId'>Scenario</label>
                        <select
                            id='propertyDevelopmentTypeId'
                            value={property?.propertyDevelopmentTypeId ?? ''}
                            onChange={(event) => _updatePropertyDevelopmentTypeId(event.target.value)}
                        >
                            <option value=''></option>
                            {PropertyDevelopmentTypeOptions.map((o) => (
                                <option
                                    key={o.id}
                                    value={o.id}
                                >
                                    {o.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {property?.propertyDevelopmentTypeId === PropertyDevelopmentType.Other && (
                        <div className='columns small-12 large-3'>
                            <label htmlFor='propertyDevelopmentTypeOther'>Scenario (Other)</label>
                            <input
                                type='text'
                                id='propertyDevelopmentTypeOther'
                                value={property?.propertyDevelopmentTypeOther ?? ''}
                                onChange={(event) => _updatePropertyDevelopmentTypeOther(event.target.value)}
                                maxLength={500}
                            />
                        </div>
                    )}
                    <div className='columns small-12 large-2'>
                        <label htmlFor='yearBuilt'>Year Built</label>
                        <input
                            type='text'
                            id='yearBuilt'
                            value={property?.yearBuilt ?? ''}
                            onChange={(event) => _updateYearBuilt(event.target.value)}
                            maxLength={4}
                        />
                    </div>
                    <div className='columns small-12 large-2'>
                        <label htmlFor='ownedThroughDevelopment'>Owned Through Dev?</label>
                        <select
                            id='ownedThroughDevelopment'
                            value={property?.ownedThroughDevelopment == null ? '' : property.ownedThroughDevelopment ? '1' : '0'}
                            onChange={(event) =>
                                _updateOwnedThroughDevelopment(event.target.value === '' ? null : event.target.value === '1')
                            }
                        >
                            <option value=''></option>
                            <option value='0'>No</option>
                            <option value='1'>Yes</option>
                        </select>
                    </div>
                    <div className='columns small-12 large-2'>
                        <label htmlFor='buildTypeId'>Site Built / Manufactured</label>
                        <select
                            id='buildTypeId'
                            value={property?.buildTypeId ?? ''}
                            onChange={(event) => _updateBuildTypeId(event.target.value)}
                        >
                            <option value=''></option>
                            <option value={buildTypes.SiteBuilt}>{buildTypeString(buildTypes.SiteBuilt)}</option>
                            <option value={buildTypes.Manufactured}>{buildTypeString(buildTypes.Manufactured)}</option>
                        </select>
                    </div>
                    <div className='columns small-12 large-3'>
                        <label htmlFor='certificateOfOccupancyTypeId'>Certificate of Occupancy</label>
                        <select
                            id='certificateOfOccupancyTypeId'
                            value={property?.certificateOfOccupancyTypeId ?? ''}
                            onChange={(event) => _updateCertificateOfOccupancyTypeId(event.target.value)}
                        >
                            <option value=''></option>
                            {CertificateOfOccupancyTypeOptions.map((o) => (
                                <option
                                    key={o.id}
                                    value={o.id}
                                >
                                    {o.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='columns small-12 large-2'>
                        <label htmlFor='workNeeded'>Work Needed?</label>
                        <select
                            id='workNeeded'
                            value={property?.workNeeded == null ? '' : property.workNeeded ? '1' : '0'}
                            onChange={(event) => _updateWorkNeeded(event.target.value === '' ? null : event.target.value === '1')}
                        >
                            <option value=''></option>
                            <option value='0'>No</option>
                            <option value='1'>Yes</option>
                        </select>
                    </div>
                    {property?.workNeeded && (
                        <div className='columns small-12 large-4'>
                            <label htmlFor='workNeededDescription'>Work Description</label>
                            <input
                                type='text'
                                id='workNeededDescription'
                                value={property?.workNeededDescription ?? ''}
                                onChange={(event) => _updateWorkNeededDescription(event.target.value)}
                                maxLength={500}
                            />
                        </div>
                    )}
                    <div className='columns small-12 large-2'>
                        <label htmlFor='exitType'>Exit Type</label>
                        <select
                            id='exitType'
                            value={property?.exitType ?? ''}
                            onChange={(event) => _updateExitType(event.target.value)}
                        >
                            <option value=''></option>
                            {ExitTypeOptions.map((o) => (
                                <option
                                    key={o.id}
                                    value={o.id}
                                >
                                    {o.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    {property?.exitType == ExitType.Sale && (
                        <div className='columns small-12 large-3'>
                            <label htmlFor='expectedListingPrice'>Expected Listing Price</label>
                            <NumericFormat
                                thousandSeparator=','
                                prefix='$'
                                id='expectedListingPrice'
                                placeholder='$'
                                value={property?.expectedListingPrice ?? ''}
                                decimalScale={0}
                                allowNegative={false}
                                onValueChange={(values) => _updateExpectedListingPrice(values.floatValue)}
                                isAllowed={(values) => {
                                    const value = values?.floatValue;
                                    return value === undefined || value === null || value >= 0;
                                }}
                            />
                        </div>
                    )}
                    {property?.exitType == ExitType.Refinance && (
                        <div className='columns small-12 large-2'>
                            <label htmlFor='expectedTakeOutLoanAmount'>Expected Take-Out Loan Amount</label>
                            <NumericFormat
                                thousandSeparator=','
                                prefix='$'
                                id='expectedTakeOutLoanAmount'
                                placeholder='$'
                                value={property?.expectedTakeOutLoanAmount ?? ''}
                                decimalScale={0}
                                allowNegative={false}
                                onValueChange={(values) => _updateExpectedTakeOutLoanAmount(values.floatValue)}
                                isAllowed={(values) => {
                                    const value = values?.floatValue;
                                    return value === undefined || value === null || value >= 0;
                                }}
                            />
                        </div>
                    )}
                    <div className='columns small-12 large-2'>
                        <label htmlFor='hoaDues'>HOA Dues?</label>
                        <select
                            id='hoaDues'
                            value={property?.hoaDues == null ? '' : property.hoaDues ? '1' : '0'}
                            onChange={(event) => _updateHoaDues(event.target.value === '' ? null : event.target.value === '1')}
                        >
                            <option value=''></option>
                            <option value='0'>No</option>
                            <option value='1'>Yes</option>
                        </select>
                    </div>
                    <div className='columns small-12 large-3'>
                        <label htmlFor='preferredInterestPayment'>Preferred Interest Payment</label>
                        <select
                            id='preferredInterestPayment'
                            value={property?.preferredInterestPaymentTypeId ?? ''}
                            onChange={(event) => _updatePreferredInterestPaymentType(event.target.value)}
                        >
                            <option value=''></option>
                            {PreferredInterestPaymentTypeOptions.sort((o) => o.sortOrder).map((o) => (
                                <option
                                    key={o.id}
                                    value={o.id}
                                >
                                    {o.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
            {isPortfolio && (
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div className='columns small-12 large-2'>
                        <label htmlFor='propCommitment'>Approved Amt {required && <span className='req'>*</span>}</label>
                        <NumericFormat
                            thousandSeparator=','
                            prefix='$'
                            id='propCommitment'
                            placeholder='$'
                            value={property.commitment ?? ''}
                            decimalScale={2}
                            allowNegative={false}
                            onValueChange={(values) => {
                                _updateCommitment(values.floatValue);
                            }}
                            required={required}
                            isAllowed={(values, sourceInfo) => {
                                const value = values?.floatValue;
                                return value === undefined || value === null || value > 0;
                            }}
                        />
                    </div>
                    <div
                        className='columns small-12 large-2'
                        style={styles.formColumn}
                    >
                        <label htmlFor='originationFee'>Origination Fee</label>
                        <NumericFormat
                            prefix='$'
                            thousandSeparator=','
                            id='originationFee'
                            placeholder='$'
                            value={property.originationFee ?? ''}
                            decimalScale={2}
                            allowNegative={false}
                            onValueChange={(values) => {
                                _updateOriginationFee(values.floatValue);
                            }}
                            required={false}
                            isAllowed={(values, sourceInfo) => {
                                const value = values?.floatValue;
                                return value === undefined || value === null || value > 0;
                            }}
                        />
                    </div>
                    <div
                        className='columns small-12 large-2'
                        style={styles.formColumn}
                    >
                        <label htmlFor='commitedAmount'>Committed Amt</label>
                        <NumericFormat
                            disabled
                            prefix='$'
                            thousandSeparator=','
                            id='commitedAmount'
                            placeholder='$'
                            value={property.originationFee + property.commitment}
                            decimalScale={2}
                            allowNegative={false}
                            required={false}
                            isAllowed={(values, sourceInfo) => {
                                const value = values?.floatValue;
                                return value === undefined || value === null || value > 0;
                            }}
                        />
                        <span className='helper'>
                            <span className='tooltip-trigger'>
                                <FontAwesomeIcon icon={faQuestionCircle} />
                            </span>
                            <span className='text'>
                                This amount is the
                                <br />
                                Approved Amount + Origination Fee
                            </span>
                        </span>
                    </div>
                </div>
            )}

            {property.id > 0 && (
                <>
                    {(statusId === LoanStatusType.Approved ||
                        statusId === LoanStatusType.Outstanding ||
                        statusId === LoanStatusType.Repaid) &&
                        loanTypeId !== LoanType.Rental &&
                        loanTypeId !== LoanType.Bridge &&
                        areDrawsEnabled && (
                            <DrawPanel
                                property={property}
                                statusId={statusId}
                                styles={styles}
                                validateSubmission={validateSubmission}
                                onProblemPropertyChange={(isProblemProperty) => onUpdateIsProblemProperty(isProblemProperty)}
                            />
                        )}
                    <AppraisalsGrid
                        title='Property Appraisals'
                        appraisals={property.appraisals}
                        onChange={(appraisals) => onAppraisalsChange(appraisals)}
                    />
                    <LoanFilesGrid
                        title='Property Files'
                        fileTypeOptions={LoanPropertyFileTypeOptions}
                        files={property.files}
                        onChange={(files) => onPropertyFilesChanges(files)}
                    />

                    <FloodReport
                        floodReport={property.floodReport}
                        canOrder={canOrderFloodReport}
                        onCancel={onFloodReportCancel}
                        onChange={(fr) => {
                            onFloodReportChange(fr);
                        }}
                    />
                    <div
                        className='row'
                        style={styles.formRow}
                    >
                        <div className='columns small-12 large-4'>
                            <label
                                htmlFor='inSpecialFloodHazardArea'
                                style={styles.leftLabel}
                            >
                                Result <span className='req'>*</span>
                            </label>
                            <select
                                id='inSpecialFloodHazardArea'
                                value={property?.inSpecialFloodHazardArea ? '1' : '2'}
                                onChange={(event) => setInSpecialFloodHazardArea(parseInt(event.target.value))}
                                required
                            >
                                {FloodAreaOptions.map((fs) => {
                                    return (
                                        <option
                                            key={fs.id}
                                            value={fs.id}
                                        >
                                            {fs.name}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <div className='columns small-12 large-3'>
                            <label
                                htmlFor='doNotSendFloodNotice'
                                style={styles.leftLabel}
                            >
                                Notice
                            </label>
                            <select
                                id='doNotSendFloodNotice'
                                value={property.doNotSendFloodNotice ? '1' : '2'}
                                onChange={(event) => setDoNotSendFloodNotice(parseInt(event.target.value))}
                                required
                                disabled={!willTriggerFloodNotice()}
                            >
                                <option value='1'>Do Not Send Notice</option>
                                <option value='2'>Send Notice</option>
                            </select>
                        </div>
                    </div>
                    <div
                        className='row'
                        style={styles.formRow}
                    >
                        <div
                            className='columns small-12 large-12'
                            style={getFloodNoticeStyle()}
                        >
                            <i title={getFloodNoticeTitle()}>
                                <FontAwesomeIcon icon={faPencil} />
                            </i>
                            &nbsp;&nbsp;{getFloodNoticeTitle()}
                        </div>
                    </div>
                </>
            )}
            {property.id === 0 && (
                <>
                    <div style={{ marginTop: '10px', color: 'silver' }}>
                        <h2 className='decorated'>Property Appraisals</h2>
                        Available after first save.
                    </div>
                    <div style={{ marginTop: '10px', color: 'silver' }}>
                        <h2 className='decorated'>Property Files</h2>
                        Available after first save.
                    </div>
                    <div style={{ marginTop: '10px', color: 'silver' }}>
                        <h2 className='decorated'>Flood Determination</h2>
                        Available after first save.
                    </div>
                </>
            )}

            {loanTypeId === LoanType.Rental && (
                <>
                    <h2
                        className='decorated'
                        style={styles.sectionHeader}
                    >
                        Property Rental Data
                    </h2>
                    <div className='row'>
                        <div
                            className='columns small-12 large-2'
                            style={styles.formColumn}
                        >
                            <label htmlFor='rentalHoldback'>Holdback {required && <span className='req'>*</span>}</label>
                            <NumericFormat
                                thousandSeparator=','
                                prefix='$'
                                id='rentalHoldback'
                                name='rentalHoldback'
                                placeholder='$'
                                value={property.rentalHoldback ?? ''}
                                decimalScale={2}
                                allowNegative={false}
                                onValueChange={(values) => {
                                    _updateRentalHoldback(values.floatValue);
                                }}
                                required={required}
                                isAllowed={(values, sourceInfo) => {
                                    const value = values?.floatValue;
                                    return value === undefined || value === null || (value >= 0 && value <= commitment);
                                }}
                            />
                        </div>
                        {property.rentalHoldback > 0 && property.id > 0 && (
                            <div
                                className='columns small-12 large-10'
                                style={styles.formColumn}
                            >
                                <label htmlFor='holdbackReleases'>Holdback Releases</label>
                                <RentalHoldbackReleasesGrid
                                    id='holdbackReleases'
                                    propertyId={property.id}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
            {loanTypeId !== LoanType.MasterNote && property.id > 0 && <RepaymentsGrids propertyId={property.id} />}
        </div>
    );
};
